/**
 * P17's compliance gate — **open in every stage since D170 (2026-08-18). The two coordinate inputs
 * capture, and this file is the whole of what decides that.**
 *
 * This was never caution about a feature. It is the PRD's own Definition of done, and D92's words
 * are unambiguous: *"the moment the picker's coordinate inputs ship, the notice-at-collection and
 * DPIA (D73) must already be in place, so the later PRD carries that gate, not the pilot."* **It
 * was a gate, not a follow-up** — and the reason it was built as a flag is that it was always
 * going to be answered by someone other than the person writing the picker.
 *
 * **What D170 decided, and what it deliberately did not.** `d73-engagement.md` Q4 asked whether
 * storing a private residence's position at ~6 decimals **from Stage 1** is proportionate when
 * D108 left the **Stage-2 map as the only consumer**. That document's own recommendation was
 * **defer capture**, and D170 **reversed it without refuting it** — the argument stands unedited
 * in §A4. The reversal rests on **irreversibility rather than need**: the driveway pin exists only
 * while a technician is standing at the gate, D51's Device-settings fallback recovers a
 * desk-entered postal centroid rather than that pin, and **P17 never built that surface anyway**,
 * so deferral forgoes the accuracy permanently on every gate installed before the map ships.
 *
 * **What is still open, and why this stays a flag rather than becoming a deleted branch.** Part D's
 * sign-off table is **still empty** — counsel has not been engaged, and Parts A/B now review
 * processing that is *running* rather than proposed. A contrary answer **re-closes these three
 * booleans**; nothing migrates, and the closed path below is still exercised by
 * `LocationPicker.test.tsx`. **Do not delete the gate because it is open.**
 *
 * **Two named consequences ride on it, and one of them shrank on the way in.** The reinstated
 * **Q2** covers a browser→Google transfer that is live in Stage 1 — but D169 retired Geocoding and
 * D170 deleted the reverse geocode, so what crosses is **the address text a technician types into
 * Places, never the device fix**. A captured coordinate now reaches no third party. Google is
 * still an *independent controller* for Maps Platform rather than a processor, so the question is
 * narrowed and not closed. **Q24** is untouched: if Q8 lands on consent, `lat`/`lng` must be
 * withdrawable independently of deleting the gate, and that control **does not exist on any
 * surface today**.
 *
 * **Why the absent Maps key is not this gate.** It looks like one — with no key there is no Places
 * search — but **"use current location" needs no key at all**. It reads the
 * browser's Geolocation API and stores the raw fix, so a build with HUMAN-GUIDE row 9 undischarged
 * would still capture and store coordinates from the first install. The key gates *cost*; this
 * gates *capture*, and they are different things.
 *
 * **What is left running while the gate is closed** is exactly what P7 shipped: a free-text address
 * with `autocomplete="street-address"`, required, stored `source: manual` with no coordinates. That
 * is not a degraded mode — it is the MVP, unchanged.
 */
import { STAGE } from '../../../auth/config'

/**
 * Per stage, so the answer can be applied — or withdrawn — one deployment at a time rather than
 * all at once. **D170 set all three to `true` in one change; the per-stage shape is retained
 * because a reversal is likelier to be partial than a rollout was.**
 *
 * **The fallback for an unknown stage is still `false`, and that is not vestigial** — a new stage
 * name must not open a privacy gate by being unlisted, which is a different rule from "the gate is
 * closed" and survives the gate being open. `LocationPicker.test.tsx` asserts both halves.
 *
 * Changing an entry is a reviewed act. `d73-engagement.md` Part D's sign-off table is **still
 * empty**, so what authorizes the current value is D170 rather than counsel; Part B's notice is
 * drafted in Hebrew and English with two placeholders open (Q5's retention window, Inforu's
 * processing locations) and **says nothing about Google**, which is the reinstated Q2. Do not flip
 * an entry to unblock a demo — `readDevOverride()` below exists for that and cannot reach a
 * deployed bundle.
 */
const COORDINATE_INPUTS_ENABLED_BY_STAGE: Readonly<Record<string, boolean>> = {
  dev: true,
  staging: true,
  prod: true,
}

const DEV_OVERRIDE_KEY = 'selogate.dev.locationCoordinateInputs'

/**
 * A dev-server-only override, in the shape `screens/shabbat/featureFlag.ts` had before R9 made
 * the שעון שבת gate a remote flag with no override (`config/`, D328's *no fourth source*) —
 * including the reason it is safe. `import.meta.env.DEV` is baked to `false` by `vite build`, so this branch does
 * not exist in any bundle `infra/web.ts` deploys, the same way `dev/devTransport.ts` does not.
 *
 * `?locationInputs=on`/`off` sets it and persists the choice, so a reload without the query string
 * keeps it. **Since D170 the useful direction is `off`** — it is how the closed path is exercised
 * locally against the dev transport now that every stage is open, and the closed path is the one a
 * contrary answer from counsel would restore.
 */
function readDevOverride(): boolean | undefined {
  if (!import.meta.env.DEV || typeof window === 'undefined') return undefined
  const requested = new URLSearchParams(window.location.search).get('locationInputs')
  if (requested === 'on' || requested === 'off') {
    window.localStorage.setItem(DEV_OVERRIDE_KEY, String(requested === 'on'))
  }
  const stored = window.localStorage.getItem(DEV_OVERRIDE_KEY)
  return stored === null ? undefined : stored === 'true'
}

/**
 * Whether D51's `device` and `places` inputs may be offered at all.
 *
 * Read once at module load, as the שעון שבת gate was: there is no user-facing toggle in
 * anything `infra/web.ts` deploys, and a gate a resident could flip would not be a gate.
 */
export const COORDINATE_INPUTS_ENABLED: boolean =
  readDevOverride() ?? COORDINATE_INPUTS_ENABLED_BY_STAGE[STAGE] ?? false
